import React, { useEffect } from "react";
import PropTypes from "prop-types";
import {
  Type,
  Spinner,
  DialogTitle,
  DialogTitleActions,
  Dialog,
} from "@ebscois/react-component-library";

import {
  ShareDialogContent,
  ShareDialogHint,
  ShareLinkLabel,
  ShareLinkDescription,
  ShareDialogTitle,
  ShareCopyLinkWrapper,
  ShareDialogDivider,
} from "./_.styled";
import CopyLink from "../CopyLink";
import { useDispatch, useStore } from "../../store";
import { fetchShareLinkData } from "../../store/actions";

const ContentShareDialog = ({
  isOpen,
  onCancel,
  pubType,
  slug,
  productCode,
}) => {
  const {
    isFetchingForShareData,
    shareLink,
    internalShareLink,
    externalShareLink,
    shareDetailsDataError,
  } = useStore();
  const dispatch = useDispatch();

  const shouldRenderBothLinks = internalShareLink && externalShareLink;

  useEffect(() => {
    if (isOpen) {
      fetchShareLinkData(dispatch, {
        location: window.location,
        pubType,
        slug,
        productCode,
      });
    }
  }, [dispatch, productCode, pubType, slug, isOpen]);

  const renderInternalShareLink = () =>
    internalShareLink ? (
      <>
        {shouldRenderBothLinks && <ShareDialogDivider horizontal />}
        <>
          <ShareLinkLabel
            dangerouslySetInnerHTML={{
              __html: shareLink.internalShareLabel,
            }}
          />
          <ShareCopyLinkWrapper>
            <CopyLink
              label="Link copied"
              btnLabel="Copy Link"
              url={internalShareLink}
            />
          </ShareCopyLinkWrapper>
          <ShareLinkDescription>
            {shareLink.internalLinkDescription}
          </ShareLinkDescription>
        </>
      </>
    ) : null;

  const renderExternalShareLink = () =>
    externalShareLink ? (
      <>
        {shouldRenderBothLinks && (
          <>
            <ShareDialogDivider horizontal />
            <ShareLinkLabel
              dangerouslySetInnerHTML={{
                __html: shareLink.externalShareLabel,
              }}
            />
          </>
        )}
        <ShareCopyLinkWrapper isSingle={!shouldRenderBothLinks}>
          <CopyLink
            label="Link copied"
            btnLabel="Copy Link"
            url={externalShareLink}
          />
        </ShareCopyLinkWrapper>
      </>
    ) : null;

  return (
    <Dialog
      onRequestClose={onCancel}
      isOpen={isOpen}
      maxWidth="lg"
      hasMaxHeight={true}
    >
      <>
        <DialogTitle paddingBottom="sm">
          <ShareDialogTitle
            element="h2"
            fontFamily="secondary"
            color="gray800"
            marginTop="md"
            marginLeft="md"
          >
            {shareLink?.title}
          </ShareDialogTitle>
          <DialogTitleActions onRequestClose={onCancel} isFloating={true} />
        </DialogTitle>
        <ShareDialogContent>
          {isFetchingForShareData ? (
            <Spinner />
          ) : (
            <>
              {shareDetailsDataError && (
                <Type>{shareDetailsDataError.message}</Type>
              )}
              {!shareDetailsDataError && (
                <>
                  <ShareDialogHint
                    dangerouslySetInnerHTML={{
                      __html: shareLink.hint,
                    }}
                  />
                  {renderInternalShareLink()}
                  {renderExternalShareLink()}
                </>
              )}
            </>
          )}
        </ShareDialogContent>
      </>
    </Dialog>
  );
};

ContentShareDialog.propTypes = {
  isOpen: PropTypes.bool,
  onCancel: PropTypes.func.isRequired,
  pubType: PropTypes.string,
  slug: PropTypes.string,
  productCode: PropTypes.string,
};

ContentShareDialog.defaultProps = {
  isOpen: false,
};

ContentShareDialog.displayName = "ContentShareDialog";

export default ContentShareDialog;
